import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { AuthImage } from "@/components/AuthImage";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

export function ImageZoomModal({ path, alt = "Textbook page", onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const clampScale = (s) => Math.min(5, Math.max(1, s));
  const zoom = (delta) => setScale((s) => {
    const ns = clampScale(s + delta);
    if (ns === 1) setPos({ x: 0, y: 0 });
    return ns;
  });
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onWheel = (e) => { zoom(e.deltaY < 0 ? 0.5 : -0.5); };

  const onPointerDown = (e) => {
    if (scale === 1) return;
    drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
  };
  const onPointerMove = useCallback((e) => {
    if (!drag.current) return;
    setPos({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) });
  }, []);
  const onPointerUp = () => { drag.current = null; };

  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-[#1f2130]/95 backdrop-blur-sm flex flex-col" data-testid="image-zoom-modal">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-sm text-white/80">{alt} · pinch or scroll to zoom, drag to pan</span>
        <div className="flex items-center gap-2">
          <button data-testid="zoom-out" onClick={() => zoom(-0.5)} className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20"><ZoomOut size={18} /></button>
          <span className="stat text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
          <button data-testid="zoom-in" onClick={() => zoom(0.5)} className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20"><ZoomIn size={18} /></button>
          <button data-testid="zoom-reset" onClick={reset} className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20"><RotateCcw size={16} /></button>
          <button data-testid="zoom-close" onClick={onClose} className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 ml-1"><X size={18} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden grid place-items-center touch-none"
        onWheel={onWheel} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        style={{ cursor: scale > 1 ? "grab" : "default" }}>
        <div style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transition: drag.current ? "none" : "transform 0.15s" }}>
          <AuthImage path={path} alt={alt} className="max-h-[80vh] max-w-[92vw] object-contain rounded-lg select-none pointer-events-none" />
        </div>
      </div>
    </motion.div>,
    document.body
  );
}
